import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowDown, Check, Mail, Phone, CheckCircle2 } from "lucide-react";
import { buildLead, submitLead, validateLead } from "../../services/leadService";
import { acceptFiles } from "../../services/uploadService";
import { UPLOAD_ACCEPT, UPLOAD_LABEL, UPLOAD_MAX_FILES, UPLOAD_MAX_MB } from "../../data/formats";
import { uuidV4 } from "../../utils/uuid";
import { takePrefill } from "../lib/prefill";
import "./ContactSection.css";

export const CONTACT_EMAIL = "si2tsolutions@outlook.fr";
export const CONTACT_PHONE_DISPLAY = "06 18 86 94 74";
export const CONTACT_PHONE_TEL = "+33618869474";

const NEED_TYPES = [
  ["RENFORT BE", "Renfort bureau d'études"],
  ["CONCEPTION 3D", "Conception mécanique / 3D"],
  ["MISE EN PLAN", "Mise en plan"],
  ["RECONCEPTION", "Modification / reprise d'existant"],
  ["DOSSIER FABRICATION", "Dossier de fabrication"],
  ["ÉTUDE TECHNIQUE", "Structure métallique simple / étude technique"],
  ["AUTRE", "Autre"],
];

const FORMATS = [
  ["STEP", "STEP", "Modèle 3D neutre"],
  ["PDF", "PDF", "Plan imprimable"],
  ["DXF", "DXF", "Découpe / 2D"],
  ["DWG", "DWG", "2D natif"],
  ["AUTRE", "Autre", "À préciser"],
];

const EMPTY = { entreprise: "", nom: "", email: "", telephone: "", typeBesoin: "", echeance: "", description: "" };
const MIN_DESCRIPTION = 20;

const Field = ({ id, label, optional, hint, children }) => (
  <div className="cf-field">
    <label htmlFor={id} className="cf-label">
      {label}
      {optional ? <span className="cf-optional"> — optionnel</span> : <span className="cf-required"> *</span>}
    </label>
    {children}
    {hint && <span className="cf-hint" id={`${id}-hint`}>{hint}</span>}
  </div>
);

const ContactSection = () => {
  const [formData, setFormData] = useState(EMPTY);
  const [formats, setFormats] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const [availability, setAvailability] = useState("loading");
  const [status, setStatus] = useState(null);
  const [success, setSuccess] = useState(null);
  const firstFieldRef = useRef(null);
  const formRef = useRef(null);
  const submissionKey = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    fetch("/api/intake/status", { cache: "no-store", signal: controller.signal })
      .then(async (response) => response.ok && (await response.json()).accepting_requests === true)
      .then((open) => setAvailability(open ? "open" : "closed"))
      .catch(() => setAvailability("closed"))
      .finally(() => window.clearTimeout(timeout));
    return () => { controller.abort(); window.clearTimeout(timeout); };
  }, []);

  useEffect(() => {
    const apply = (data) => {
      if (!data) return;
      setFormData((prev) => ({
        ...prev,
        typeBesoin: ({ renfort: "RENFORT BE", conception: "CONCEPTION 3D", "mise-en-plan": "MISE EN PLAN", modification: "RECONCEPTION", dossier: "DOSSIER FABRICATION", structure: "ÉTUDE TECHNIQUE", autre: "AUTRE" })[data.typeBesoin] || data.typeBesoin || prev.typeBesoin,
        description: data.description ? `${data.description}${prev.description}` : prev.description,
      }));
      setSuccess(null);
    };
    apply(takePrefill());
    const onPrefill = (e) => {
      apply(e.detail);
      takePrefill();
    };
    window.addEventListener("si2t:prefill", onPrefill);
    return () => window.removeEventListener("si2t:prefill", onPrefill);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    submissionKey.current = null;
    setStatus(null);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleFormat = (id) => {
    submissionKey.current = null;
    setFormats((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  };

  const addFiles = (list) => {
    if (!list) return;
    const { accepted, overflow } = acceptFiles(uploadedFiles, Array.from(list));
    setUploadedFiles((prev) => [...prev, ...accepted]);
    submissionKey.current = null;
    if (overflow) setStatus({ type: "error", text: `${UPLOAD_MAX_FILES} fichiers maximum par demande.` });
  };

  const goToForm = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.body.classList.contains("reduce-motion");
    formRef.current?.scrollIntoView({ behavior: reduced ? "instant" : "smooth", block: "start" });
    firstFieldRef.current?.focus({ preventScroll: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || availability !== "open") return;
    setStatus(null);
    if (!e.currentTarget.reportValidity()) return;
    const values = {
      company: formData.entreprise, name: formData.nom, email: formData.email,
      phone: formData.telephone, needType: formData.typeBesoin,
      deadline: formData.echeance, description: formData.description, desiredFormats: formats,
    };
    const errors = validateLead(values, uploadedFiles);
    const firstError = Object.values(errors).find(Boolean);
    if (firstError) {
      setStatus({ type: "error", text: firstError });
      return;
    }
    setSending(true);
    try {
      submissionKey.current ||= uuidV4();
      const result = await submitLead(buildLead(values, uploadedFiles, "site"), uploadedFiles, submissionKey.current, honeypot);
      if (!result.ok) {
        setStatus({ type: "error", text: `${result.message} Votre saisie et vos fichiers sont conservés.` });
        return;
      }
      setSuccess({ reference: result.reference, files: uploadedFiles.length, email: formData.email });
      setFormData(EMPTY);
      setFormats([]);
      setUploadedFiles([]);
      submissionKey.current = null;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setTimeout(() => document.getElementById("contact-success")?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" }), 50);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-section s-section" id="contact" aria-labelledby="contact-title" data-testid="contact-section">
      <div className="s-container">
        <div className="contact-intro">
          <div className="reveal">
            <p className="s-label">Un projet&nbsp;?</p>
            <h2 id="contact-title" className="contact-title" data-testid="contact-title">Parlons-en.</h2>
            <ul className="contact-channels" aria-label="Moyens de contact">
              <li>
                <Mail size={16} aria-hidden="true" />
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link">{CONTACT_EMAIL}</a>
              </li>
              <li>
                <Phone size={16} aria-hidden="true" />
                <a href={`tel:${CONTACT_PHONE_TEL}`} data-testid="contact-phone-link">{CONTACT_PHONE_DISPLAY}</a>
              </li>
            </ul>
          </div>
          <div className="contact-intro-side reveal" data-delay="1">
            <p className="s-lead">
              Une pièce à concevoir, un plan à reprendre ou un dossier à finaliser&nbsp;?
              Décrivez votre besoin, vos contraintes et les éléments disponibles pour préparer l'échange avec SI2T.
            </p>
            {availability === "open" && <button type="button" className="btn btn-primary" onClick={goToForm} data-testid="contact-cta">
              Présenter mon besoin
              <ArrowDown size={16} strokeWidth={2.2} />
            </button>}
          </div>
        </div>

        {success && (
          <div className="contact-success reveal is-visible" id="contact-success" role="status" data-testid="contact-success">
            <CheckCircle2 size={22} aria-hidden="true" />
            <div>
              <p className="contact-success-title">Demande enregistrée — référence {success.reference}</p>
              <p>
                SI2T a bien reçu votre demande{success.files > 0 ? ` et ${success.files} pièce(s) jointe(s)` : ""}.
                La réponse vous sera adressée à <strong>{success.email}</strong>. Conservez la référence pour tout échange.
              </p>
            </div>
          </div>
        )}

        {availability !== "open" && <div className="contact-form" role="status" data-testid="contact-availability">
          {availability === "loading" ? "Vérification de la disponibilité du formulaire…" : "Le formulaire est temporairement indisponible. Contactez SI2T par email ou téléphone."}
        </div>}

        {availability === "open" && <form className="contact-form" ref={formRef} onSubmit={handleSubmit} noValidate data-testid="contact-form">
          <p className="cf-form-intro">Décrivez votre besoin, même si certains points restent à définir. Les champs marqués d'un astérisque (*) sont obligatoires. Les pièces jointes sont facultatives.</p>
          <fieldset className="cf-block">
            <legend className="cf-legend">
              <span className="cf-legend-num">01</span> Vos coordonnées
            </legend>
            <div className="cf-grid">
              <Field id="entreprise" label="Entreprise">
                <input ref={firstFieldRef} type="text" id="entreprise" name="entreprise" className="cf-input" required minLength={2} maxLength={120} autoComplete="organization" value={formData.entreprise} onChange={handleChange} data-testid="contact-entreprise" />
              </Field>
              <Field id="nom" label="Nom">
                <input type="text" id="nom" name="nom" className="cf-input" required minLength={2} maxLength={120} autoComplete="name" value={formData.nom} onChange={handleChange} data-testid="contact-nom" />
              </Field>
              <Field id="email" label="Email">
                <input type="email" id="email" name="email" className="cf-input" required autoComplete="email" value={formData.email} onChange={handleChange} data-testid="contact-email" />
              </Field>
              <Field id="telephone" label="Téléphone" optional>
                <input type="tel" id="telephone" name="telephone" className="cf-input" maxLength={40} autoComplete="tel" value={formData.telephone} onChange={handleChange} data-testid="contact-telephone" />
              </Field>
            </div>
            <div className="cf-honeypot" aria-hidden="true">
              <label htmlFor="website">Site web</label>
              <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </div>
          </fieldset>

          <fieldset className="cf-block">
            <legend className="cf-legend">
              <span className="cf-legend-num">02</span> Votre besoin
            </legend>
            <div className="cf-grid">
              <Field id="typeBesoin" label="Type de besoin">
                <select id="typeBesoin" name="typeBesoin" className="cf-input cf-select" required value={formData.typeBesoin} onChange={handleChange} data-testid="contact-type">
                  <option value="">— Sélectionner —</option>
                  {NEED_TYPES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </Field>
              <Field id="echeance" label="Échéance souhaitée" optional>
                <input type="date" id="echeance" name="echeance" className="cf-input" value={formData.echeance} onChange={handleChange} data-testid="contact-echeance" />
              </Field>
            </div>
            <Field id="description" label="Description du besoin" hint={`${MIN_DESCRIPTION} caractères minimum · ${formData.description.length}/5000`}>
              <textarea id="description" name="description" className="cf-input cf-textarea" rows="6" required minLength={MIN_DESCRIPTION} maxLength={5000} aria-describedby="description-hint" placeholder="Que souhaitez-vous concevoir, modifier ou finaliser ? Précisez la pièce ou l'ensemble concerné, les contraintes connues et les documents disponibles." value={formData.description} onChange={handleChange} data-testid="contact-description" />
            </Field>

            <div className="cf-field">
              <span className="cf-label">
                Format de livraison souhaité<span className="cf-optional"> — optionnel</span>
              </span>
              <div className="cf-formats" role="group" aria-label="Format de livraison souhaité">
                {FORMATS.map(([id, label, desc]) => {
                  const checked = formats.includes(id);
                  return (
                    <button type="button" key={id} className={`cf-format ${checked ? "is-checked" : ""}`} aria-pressed={checked} onClick={() => toggleFormat(id)} data-testid={`contact-format-${id}`}>
                      <span className="cf-format-check">{checked && <Check size={12} strokeWidth={3} />}</span>
                      <span className="cf-format-name">{label}</span>
                      <span className="cf-format-desc">{desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </fieldset>

          <fieldset className="cf-block">
            <legend className="cf-legend">
              <span className="cf-legend-num">03</span> Pièces jointes <span className="cf-optional">— optionnel</span>
            </legend>
            <p className="cf-help">
              Croquis, plans existants, photos ou documents techniques. <strong>{UPLOAD_MAX_FILES} fichiers maximum, {UPLOAD_MAX_MB} Mo par fichier</strong> —
              {UPLOAD_LABEL}. Les fichiers sont transmis uniquement avec la demande.
            </p>
            <label className="cf-file-picker" htmlFor="contact-files">Ajouter des pièces jointes</label>
            <input id="contact-files" type="file" multiple accept={UPLOAD_ACCEPT.join(",")} onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} data-testid="upload-input" />
            {uploadedFiles.length > 0 && (
              <div className="cf-attached" data-testid="contact-attached-count">
                <p>{uploadedFiles.length} pièce(s) jointe(s) sélectionnée(s).</p>
                <ul aria-label="Fichiers sélectionnés">
                  {uploadedFiles.map((file) => <li key={file.id}>
                    <span>{file.name}{file.error ? ` — ${file.error}` : ""}</span>
                    <button type="button" onClick={() => { setUploadedFiles((prev) => prev.filter((item) => item.id !== file.id)); submissionKey.current = null; }} aria-label={`Retirer ${file.name}`}>Retirer</button>
                  </li>)}
                </ul>
              </div>
            )}
          </fieldset>

          <div className="cf-footer">
            <button type="submit" className="btn btn-primary cf-submit" disabled={sending} aria-busy={sending} data-testid="contact-submit">
              {sending ? "Enregistrement…" : "Envoyer ma demande"}
              {!sending && <ArrowRight size={16} strokeWidth={2.2} />}
            </button>
            <ol className="cf-steps" aria-label="Déroulé">
              <li>Vous transmettez votre besoin</li>
              <li>SI2T analyse le dossier</li>
              <li>Vous recevez une proposition</li>
              <li>L'étude démarre après validation</li>
            </ol>
          </div>

          {status && (
            <p className={`cf-status is-${status.type}`} role="alert" data-testid="contact-status">
              {status.text}
            </p>
          )}
          <p className="cf-demo" data-testid="contact-privacy-note">
            Vos informations servent uniquement au traitement de votre demande par SI2T.
          </p>
        </form>}
      </div>
    </section>
  );
};

export default ContactSection;
